import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import "../../Style/GlobalAIAssistant.css";
import { ContextExtractor, AIService } from "./GlobalAIAssistantHelper";
import axios from "axios";
import { path_url } from "../../Config/config";
import GuestIdentityForm from "./GuestIdentityForm";
import { isLoggedIn as checkLoggedIn } from "../../Utils/auth";
import { useCurrentUser } from "../../context/UserContext";

/* ============================================================
   ENTELIPRO ERP AI Assistant — Customer Care & Support Engine
   Features:
   - Logged-in vs Guest onboarding flow (Personalized greeting vs Identity form)
   - Spring Boot GET /api/user/profile integration
   - Guided Amazon/Flipkart/Jio Care style interactive menus & quick reply buttons
   - Sub-menus, Navigation stack (Home, Back, Restart, Close)
   - Yes/No feedback & Conversation ending flow
   - Direct integration with Spring Boot -> Python AI backend
   ============================================================ */

/* ─── SVG Icons ─────────────── */
const IcBot = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="10" rx="3" />
    <path d="M12 11V7" />
    <circle cx="12" cy="5" r="2" />
    <circle cx="7.5" cy="15.5" r="1" fill="currentColor" stroke="none" />
    <circle cx="16.5" cy="16.5" r="1" fill="currentColor" stroke="none" />
    <path d="M9 19h6" />
  </svg>
);

const IcClose = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IcSend = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" fill="currentColor" stroke="none" />
  </svg>
);

const IcClock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);

const IcHome = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const IcBack = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const IcRestart = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
  </svg>
);

const IcBook = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const IcAdmin = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

/* ── Menu Categories & Guided Sub-menus ── */
const MAIN_MENU_OPTIONS = [
  "📋 Lead Management",
  "👥 Customer Management",
  "🏭 Vendor Management",
  "📦 Inventory",
  "📑 Purchase Orders",
  "🧾 Invoices & Billing",
  "📊 CRM & Sales",
  "📈 Reports & Analytics",
  "🤖 AI Features",
  "ℹ️ About ENTELIPRO ERP",
  "💬 Talk to AI"
];

const SUB_MENUS = {
  "📋 Lead Management": {
    intro: "📋 **Lead Management**: Track, qualify, and nurture prospects to boost conversion rates across your sales pipeline.",
    options: ["Create Lead", "Lead Pipeline", "Lead Status & Scoring", "Go Back ↩️", "Main Menu 🏠"]
  },
  "👥 Customer Management": {
    intro: "👥 **Customer Management**: Maintain complete customer profiles, order histories, interaction logs, and support metrics.",
    options: ["Customer Profile", "Engagement History", "Re-engagement Tactics", "Go Back ↩️", "Main Menu 🏠"]
  },
  "🏭 Vendor Management": {
    intro: "🏭 **Vendor Management**: Evaluate supplier performance, manage contracts, track goods receipts, and streamline vendor payments.",
    options: ["Vendor Performance", "Purchase RFQ", "Vendor Invoices", "Go Back ↩️", "Main Menu 🏠"]
  },
  "📦 Inventory": {
    intro: "📦 **Inventory Control**: Real-time multi-warehouse stock monitoring, batch/serial tracking, reorder alerts, and 2D/3D maps.",
    options: ["Stock Control", "Reorder Level Alerts", "Warehouse Maps & Bins", "Go Back ↩️", "Main Menu 🏠"]
  },
  "📑 Purchase Orders": {
    intro: "📑 **Purchase Orders**: Automate procurement from Purchase Requests (PR) and RFQs to PO issuance and GRN reconciliation.",
    options: ["Purchase Request (PR)", "Goods Receipt (GRN)", "Vendor Payments", "Go Back ↩️", "Main Menu 🏠"]
  },
  "🧾 Invoices & Billing": {
    intro: "🧾 **Invoices & Billing**: Automated tax invoicing, credit notes, recurring subscription billing, and general ledger posting.",
    options: ["Create Invoice", "Overdue Invoices", "Payment Reminders", "Go Back ↩️", "Main Menu 🏠"]
  },
  "📊 CRM & Sales": {
    intro: "📊 **CRM & Sales**: Unified deal tracking, price lists, quotations, sales forecasting, and commission structures.",
    options: ["Deal Pipeline", "Quotations", "Sales Forecast", "Go Back ↩️", "Main Menu 🏠"]
  },
  "📈 Reports & Analytics": {
    intro: "📈 **Reports & Analytics**: Real-time executive dashboards, cash flow forecasting, sales velocity reports, and custom exports.",
    options: ["Sales Reports", "Cash Flow Analytics", "Inventory Reports", "Go Back ↩️", "Main Menu 🏠"]
  },
  "🤖 AI Features": {
    intro: "🤖 **ENTELIPRO AI**: 14 specialized AI agents, EyeSense Vision OCR, document auto-parsing, and predictive analytics.",
    options: ["EyeSense Vision OCR", "AI Agent Types", "Predictive Analytics", "Go Back ↩️", "Main Menu 🏠"]
  },
  "ℹ️ About ENTELIPRO ERP": {
    intro: "ℹ️ **About ENTELIPRO ERP**: Cloud-native, AI-first Enterprise ERP/CRM built to unify multi-department workflows seamlessly.",
    options: ["Core Principles", "Supported Industries", "Free Trial", "Go Back ↩️", "Main Menu 🏠"]
  }
};

/* ── Render **bold** markdown & line breaks ── */
const RenderText = ({ text }) => {
  if (!text) return null;
  const lines = text.split("\n");
  return (
    <>
      {lines.map((line, li) => {
        const parts = line.split(/\*\*(.+?)\*\*/g);
        return (
          <span key={li}>
            {parts.map((p, pi) =>
              pi % 2 === 1 ? <strong key={pi}>{p}</strong> : p
            )}
            {li < lines.length - 1 && <br />}
          </span>
        );
      })}
    </>
  );
};

const timeNow = () =>
  new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
const GlobalAIAssistant = ({ isOpen: isOpenProp, onToggle }) => {
  const location = useLocation();

  /* Open/close state */
  const [isOpenLocal, setIsOpenLocal] = useState(false);
  const isOpen = isOpenProp !== undefined ? isOpenProp : isOpenLocal;
  const setIsOpen = useCallback((val) => {
    if (onToggle) onToggle(val);
    else setIsOpenLocal(val);
  }, [onToggle]);

  /* View Mode: "chat" vs "admin" */
  const [viewMode, setViewMode] = useState("chat");

  /* Unanswered Queries state (for Admin review) */
  const [unansweredList, setUnansweredList] = useState(() => AIService.getUnansweredQueries());
  const [adminAnswerInputs, setAdminAnswerInputs] = useState({});
  const [adminVolumeInputs, setAdminVolumeInputs] = useState({});
  const [filterModule, setFilterModule] = useState("all");
  const [filterStatus, setFilterStatus] = useState("pending");

  const { currentUser } = useCurrentUser();

  /* ── Auth State — reads from shared auth utility (same token Login.jsx stored) ── */
  const [isLoggedIn, setIsLoggedIn] = useState(() => checkLoggedIn());
  const [userName, setUserName] = useState(
    () => currentUser?.name || localStorage.getItem("name") || localStorage.getItem("userName") || ""
  );
  const [guestUser, setGuestUser] = useState(() => {
    try {
      const savedVisitor = localStorage.getItem("entelipro_visitor");
      return savedVisitor ? JSON.parse(savedVisitor) : null;
    } catch (error) {
      console.error("Failed to restore visitor:", error);
      return null;
    }
  });

  // Sync user name whenever currentUser changes
  useEffect(() => {
    if (currentUser?.name) {
      setUserName(currentUser.name);
    } else {
      const stored = localStorage.getItem("name") || localStorage.getItem("userName");
      if (stored) setUserName(stored);
    }
  }, [currentUser]);

  // Listen for login / logout events fired by Login.jsx and the auth utility
  useEffect(() => {
    const onLogin = () => {
      setIsLoggedIn(checkLoggedIn());
      // Refresh user name from storage after login
      const stored = localStorage.getItem("name") || localStorage.getItem("userName");
      if (stored) setUserName(stored);
    };
    const onLogout = () => {
      setIsLoggedIn(false);
      setGuestUser(null);
    };

    // Listen to both the new event names AND the legacy names for full compatibility
    window.addEventListener("entelipro:login", onLogin);
    window.addEventListener("entelipro-login", onLogin);
    window.addEventListener("user-logged-in", onLogin);
    window.addEventListener("entelipro:logout", onLogout);
    window.addEventListener("entelipro-logout", onLogout);
    window.addEventListener("user-logged-out", onLogout);
    window.addEventListener("storage", onLogin); // cross-tab sync

    return () => {
      window.removeEventListener("entelipro:login", onLogin);
      window.removeEventListener("entelipro-login", onLogin);
      window.removeEventListener("user-logged-in", onLogin);
      window.removeEventListener("entelipro:logout", onLogout);
      window.removeEventListener("entelipro-logout", onLogout);
      window.removeEventListener("user-logged-out", onLogout);
      window.removeEventListener("storage", onLogin);
    };
  }, []);

  /* Page route check for guest form trigger */
  const currentPath = (location.pathname || "").toLowerCase();
  const isPublicPage = currentPath === "/"
    || currentPath.includes("/home")
    || currentPath.includes("/about")
    || currentPath.includes("/services")
    || currentPath.includes("/features")
    || currentPath.includes("/pricing")
    || currentPath.includes("/contact")
    || currentPath.includes("/resources")
    || currentPath.includes("/eyesense")
    || currentPath.includes("/industry");

  const showGuestForm = isPublicPage && !isLoggedIn;
  const canChat = !showGuestForm || !!guestUser;

  /* Build Greetings */
  const getGreetingText = useCallback(() => {
    if (isLoggedIn) {
      const name = userName ? userName : "User";
      return `Hello **${name}** 👋\n\nWelcome back to **EnteliPro ERP**.\n\nHow can I help you today?`;
    }
    if (showGuestForm && !guestUser) {
      return `Hello! I'm your **EnteliPro AI Assistant**.\n\nBefore we begin, please share your details so I can assist you better.`;
    }
    if (guestUser) {
      return `Hello **${guestUser.name}** 👋\n\nThanks for sharing your details.\n\nNow I can help you learn about **EnteliPro ERP**. What would you like to explore?`;
    }
    return `Hello! I'm your **EnteliPro AI Assistant**.\n\nHow can I help you with EnteliPro ERP today?`;
  }, [isLoggedIn, userName, showGuestForm, guestUser]);

  /* Messages State */
  const [messages, setMessages] = useState(() => [
    {
      id: "greeting-" + Date.now(),
      type: "ai",
      text: getGreetingText(),
      time: timeNow()
    }
  ]);
  const [inputText, setInputText] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [activeFeedbackId, setActiveFeedbackId] = useState(null);
  const [isChatClosed, setIsChatClosed] = useState(false);
  const [isTalkToAIMode, setIsTalkToAIMode] = useState(false);

  /* Navigation menu state */
  const [menuStack, setMenuStack] = useState(["main"]);
  const [activeButtons, setActiveButtons] = useState(MAIN_MENU_OPTIONS);

  /* Refs */
  const conversationRef = useRef(null);
  const inputRef = useRef(null);
  const prevPathRef = useRef(location.pathname);

  /* Page change reset */
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      setMessages([
        {
          id: "greeting-" + Date.now(),
          type: "ai",
          text: getGreetingText(),
          time: timeNow()
        }
      ]);
      setInputText("");
      setIsThinking(false);
      setActiveFeedbackId(null);
      setIsChatClosed(false);
      setIsTalkToAIMode(false);
      setMenuStack(["main"]);
      setActiveButtons(MAIN_MENU_OPTIONS);
      setWindowContext(window.__CRM_PAGE_CONTEXT__ || null);
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname, getGreetingText]);

  /* Auto-scroll */
  useEffect(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [messages, isThinking, viewMode]);

  /* Focus input */
  useEffect(() => {
    if (isOpen && viewMode === "chat" && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 280);
    }
  }, [isOpen, viewMode]);

  /* Page context */
  const [windowContext, setWindowContext] = useState(() => window.__CRM_PAGE_CONTEXT__ || null);
  const getCtx = useCallback(() =>
    ContextExtractor.getPageContext(location.pathname, windowContext),
    [location.pathname, windowContext]
  );

  /* Navigation control handlers */
  const handleGoHome = () => {
    setMenuStack(["main"]);
    setActiveButtons(MAIN_MENU_OPTIONS);
    setMessages(prev => [
      ...prev,
      { id: Date.now(), type: "user", text: "Main Menu 🏠", time: timeNow() },
      { id: Date.now() + 1, type: "ai", text: "Here is the **Main Menu**. What would you like to explore?", time: timeNow() }
    ]);
  };

  const handleGoBack = () => {
    if (menuStack.length > 1) {
      const newStack = menuStack.slice(0, menuStack.length - 1);
      setMenuStack(newStack);
      const parentMenu = newStack[newStack.length - 1];
      if (parentMenu === "main") {
        setActiveButtons(MAIN_MENU_OPTIONS);
        setMessages(prev => [
          ...prev,
          { id: Date.now(), type: "user", text: "Go Back ↩️", time: timeNow() },
          { id: Date.now() + 1, type: "ai", text: "Returned to **Main Menu**.", time: timeNow() }
        ]);
      } else if (SUB_MENUS[parentMenu]) {
        setActiveButtons(SUB_MENUS[parentMenu].options);
        setMessages(prev => [
          ...prev,
          { id: Date.now(), type: "user", text: "Go Back ↩️", time: timeNow() },
          { id: Date.now() + 1, type: "ai", text: SUB_MENUS[parentMenu].intro, time: timeNow() }
        ]);
      }
    } else {
      handleGoHome();
    }
  };

  const handleRestart = () => {
    setMenuStack(["main"]);
    setActiveButtons(MAIN_MENU_OPTIONS);
    setIsChatClosed(false);
    setActiveFeedbackId(null);
    setIsTalkToAIMode(false);
    setMessages([
      {
        id: "greeting-" + Date.now(),
        type: "ai",
        text: getGreetingText(),
        time: timeNow()
      }
    ]);
  };

  /* ── Core Option / Message Handler ── */
  const handleOptionClick = async (optionText) => {
    if (isThinking) return;

    if (optionText === "Main Menu 🏠" || optionText === "Main Menu") {
      handleGoHome();
      return;
    }
    if (optionText === "Go Back ↩️" || optionText === "Go Back") {
      handleGoBack();
      return;
    }
    if (optionText === "Talk to AI" || optionText === "💬 Talk to AI") {
      setMessages(prev => [
        ...prev,
        { id: Date.now(), type: "user", text: optionText, time: timeNow() },
        {
          id: Date.now() + 1,
          type: "ai",
          text: "Sure! Type your question below and I'll answer using our AI.\n\nYou can ask anything about **EnteliPro ERP** — modules, features, pricing, or how to use specific tools.",
          time: timeNow()
        }
      ]);
      // Clear all quick buttons — user must now type freely in the input box
      setActiveButtons([]);
      setIsTalkToAIMode(true);
      setTimeout(() => inputRef.current?.focus(), 100);
      return;
    }
    if (optionText === "More Options") {
      const currentCategory = menuStack[menuStack.length - 1];
      if (SUB_MENUS[currentCategory]) {
        setActiveButtons(SUB_MENUS[currentCategory].options);
      } else {
        setActiveButtons(MAIN_MENU_OPTIONS);
      }
      return;
    }
    if (optionText === "End Conversation 🏁" || optionText === "Close Chat ❌") {
      setIsChatClosed(true);
      setMessages(prev => [
        ...prev,
        { id: Date.now(), type: "user", text: optionText, time: timeNow() },
        { id: Date.now() + 1, type: "ai", text: "Thank you for using **ENTELIPRO ERP**.\n\nHave a great day!", time: timeNow() }
      ]);
      setActiveButtons([]);
      return;
    }

    // Check if clicked a main category
    if (SUB_MENUS[optionText]) {
      setMenuStack(prev => [...prev, optionText]);
      setActiveButtons(SUB_MENUS[optionText].options);
      setMessages(prev => [
        ...prev,
        { id: Date.now(), type: "user", text: optionText, time: timeNow() },
        { id: Date.now() + 1, type: "ai", text: SUB_MENUS[optionText].intro, time: timeNow() }
      ]);
      return;
    }

    // Custom question or sub-option query
    await handleSend(optionText);
  };

  /* ── Core Send Handler (Calls Spring Boot -> Python AI) ── */
  const handleSend = useCallback(async (override) => {
    const text = (override !== undefined ? override : inputText).trim();
    if (!text || isThinking || !canChat) return;
    if (override === undefined) setInputText("");

    const newUserMsg = {
      id: Date.now(),
      type: "user",
      text,
      time: timeNow()
    };

    let updatedMessages;
    if (isChatClosed) {
      setIsChatClosed(false);
      const newGreeting = {
        id: "greeting-" + Date.now(),
        type: "ai",
        text: getGreetingText(),
        time: timeNow()
      };
      updatedMessages = [newGreeting, newUserMsg];
      setMessages(updatedMessages);
    } else {
      updatedMessages = [...messages, newUserMsg];
      setMessages(updatedMessages);
    }

    setActiveFeedbackId(null);
    setIsThinking(true);

    const ctx = getCtx();

    try {
      const reply = await AIService.ask(text, ctx.type, ctx.data, updatedMessages, guestUser);
      setUnansweredList(AIService.getUnansweredQueries());

      setMessages(prev => [
        ...prev,
        { id: Date.now() + 1, type: "ai", text: reply, time: timeNow() }
      ]);
    } catch (err) {
      const errMsg = err?.message
        ? `⚠️ ${err.message}`
        : "⚠️ I couldn't find a specific answer for that right now.\n\nTry rephrasing your question, or contact our support team at support@entelipro.com for help.";

      setMessages(prev => [
        ...prev,
        {
          id: Date.now() + 1,
          type: "ai",
          text: errMsg,
          time: timeNow()
        }
      ]);
    } finally {
      setIsThinking(false);
    }
  }, [inputText, isThinking, canChat, getCtx, messages, isChatClosed, guestUser, getGreetingText]);

  /* Feedback response */
  const handleFeedbackResponse = (id, choice) => {
    if (id !== activeFeedbackId) return;
    setActiveFeedbackId(null);

    if (choice === "yes") {
      setMessages(prev => [
        ...prev,
        { id: Date.now(), type: "user", text: "✅ Yes", time: timeNow() },
        { id: Date.now() + 1, type: "ai", text: "Great! Anything else I can help you with?", time: timeNow() }
      ]);
      setActiveButtons(["Main Menu 🏠", "Close Chat ❌"]);
    } else {
      setMessages(prev => [
        ...prev,
        { id: Date.now(), type: "user", text: "❌ No", time: timeNow() },
        { id: Date.now() + 1, type: "ai", text: "I'm sorry. Would you like to try typing your question to our AI assistant or return to the main menu?", time: timeNow() }
      ]);
      setActiveButtons(["💬 Talk to AI", "Main Menu 🏠"]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /* Admin Action */
  const handleAddAnswer = (item) => {
    const answerText = (adminAnswerInputs[item.id] || "").trim();
    const volumeText = (adminVolumeInputs[item.id] || "Volume 11 – Extended Knowledge Base").trim();

    if (!answerText) {
      alert("Please enter an answer to add to the Reference Knowledge Base.");
      return;
    }

    AIService.addKnowledgeBaseEntry(item.question, volumeText, answerText, item.id);
    setUnansweredList(AIService.getUnansweredQueries());
    setAdminAnswerInputs(prev => ({ ...prev, [item.id]: "" }));
    setAdminVolumeInputs(prev => ({ ...prev, [item.id]: "" }));
    alert(`✅ Knowledge Base Updated!\n\nThe answer for "${item.question}" has been saved.`);
  };

  const ctx = getCtx();
  const pendingCount = unansweredList.filter(q => q.status === "pending").length;

  const filteredQueries = unansweredList.filter(item => {
    if (filterStatus !== "all" && item.status !== filterStatus) return false;
    if (filterModule !== "all" && item.pageModule !== filterModule) return false;
    return true;
  });

  return (
    <>
      {/* ─── Floating Launcher ─────────────────────────── */}
      <button
        className={`gai-launcher${isOpen ? " gai-launcher--hidden" : ""}`}
        onClick={() => setIsOpen(true)}
        title="Open EnteliPro AI Assistant"
        aria-label="Open EnteliPro AI Assistant"
      >
        <svg className="gai-launcher-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2 Q12 12 2 12 Q12 12 12 22 Q12 12 12 22 Q12 12 12 2 Z" />
          <path d="M19 5 Q19 9 15 9 Q19 9 19 13 Q19 9 23 9 Q19 9 19 5 Z" opacity="0.8" />
        </svg>
      </button>

      {/* ─── Assistant Panel ─────────────────────────────── */}
      <div
        className={`gai-panel${!isOpen ? " gai-panel--hidden" : ""}`}
        role="complementary"
        aria-label="EnteliPro AI Assistant"
      >
        {/* Header */}
        <div className="gai-header">
          <div className="gai-header-brand">
            <div className="gai-header-avatar"><IcBot /></div>
            <div className="gai-header-title-wrap">
              <h3 className="gai-header-name">EnteliPro AI Assistant</h3>
              <p className="gai-header-sub">Your intelligent ERP assistant</p>
            </div>
          </div>
          <div className="gai-header-actions">
            <button
              className="gai-icon-btn gai-close-btn"
              onClick={() => setIsOpen(false)}
              title="Close"
              aria-label="Close EnteliPro AI Assistant"
            >
              <IcClose />
            </button>
          </div>
        </div>

        {/* CHAT MODE */}
        <div className="gai-conversation" ref={conversationRef}>
          {messages.map((msg) => (
            <div key={msg.id} style={{ display: "contents" }}>
              <div className={`gai-msg-group gai-msg-group--${msg.type === "user" ? "user" : "ai"}`}>
                <div className="gai-msg-meta">
                  {msg.type !== "user" && (
                    <div className="gai-msg-avatar-sm"><IcBot /></div>
                  )}
                  <span className="gai-msg-name">
                    {msg.type === "user" ? "You" : "EnteliPro AI Assistant"}
                  </span>
                  {msg.time && <span className="gai-msg-time">{msg.time}</span>}
                </div>
                <div className="gai-bubble">
                  <RenderText text={msg.text} />
                </div>
              </div>
            </div>
          ))}

          {/* Guest identity form for logged-out users */}
          {!canChat && messages.length >= 1 && (
            <div className="gai-msg-group gai-msg-group--ai">
              <GuestIdentityForm
                onSubmit={async (data) => {
                  try {

                    const response = await axios.post(`${path_url}/visitor`, {
                      name: data.name.trim(),
                      email: data.email.trim(),
                      mobile: data.mobile.trim()
                    })

                    if (response?.status === 200) {
                      const visitorId = response?.data?.id;

                      if (!visitorId) {
                        throw new Error("Visitor ID was not returned by the server.");
                      }

                      // Initialize chatbot session with Visitor ID
                      await axios.post(`${path_url}/api/ai/python/session/init`, {
                        userName: data.name.trim(),
                        referrerUrl: document.referrer || "",
                        utmSource: "",
                        utmMedium: "",
                        utmCampaign: "",
                        visitorId: visitorId
                      });

                      // Only enable chatbot after session is successfully created
                      const visitorUser = {
                        ...data,
                        visitorId: visitorId
                      };

                      setGuestUser(visitorUser);
                      localStorage.setItem("entelipro_visitor", JSON.stringify(visitorUser));

                      setMessages((prev) => [
                        ...prev,
                        {
                          type: "assistant",
                          text: `Thanks ${data.name}! You can now start chatting with me.`
                        }
                      ]);
                    } else {
                      throw new Error("Unable to save your details.");
                    }
                  } catch (error) {
                    console.error("Guest details save failed:", error);

                    alert(
                      error?.response?.data?.message ||
                      "Unable to save your details. Please try again."
                    );
                  }
                }}
              />
            </div>
          )}

          {/* Typing indicator */}
          {isThinking && (
            <div className="gai-typing-wrap">
              <div className="gai-msg-meta">
                <div className="gai-msg-avatar-sm"><IcBot /></div>
                <span className="gai-msg-name">EnteliPro AI Assistant</span>
              </div>
              <div className="gai-typing">
                <div className="gai-typing-dot" />
                <div className="gai-typing-dot" />
                <div className="gai-typing-dot" />
              </div>
            </div>
          )}
        </div>

        {/* Quick Action Buttons */}
        {activeButtons.length > 0 && !isChatClosed && canChat && (
          <div className="gai-quick-actions">
            {activeButtons.map((btn) => (
              <button
                key={btn}
                className="gai-quick-btn"
                onClick={() => handleOptionClick(btn)}
                disabled={isThinking}
              >
                {btn}
              </button>
            ))}
          </div>
        )}

        {/* Footer Input */}
        <div className="gai-footer">
          {isTalkToAIMode && (
            <div className="gai-ai-mode-bar">
              <span className="gai-ai-mode-label">🤖 AI Chat Mode — ask anything</span>
              <button
                className="gai-ai-mode-back"
                onClick={handleGoHome}
                title="Return to main menu"
              >
                ← Main Menu
              </button>
            </div>
          )}
          <div className="gai-input-row">
            <textarea
              ref={inputRef}
              className="gai-input"
              placeholder={isTalkToAIMode ? "Type your question and press Enter…" : "Ask EnteliPro AI Assistant..."}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isThinking || !canChat}
              rows={1}
              aria-label="Ask EnteliPro AI Assistant"
            />
            <button
              className="gai-send-btn"
              onClick={() => handleSend()}
              disabled={!inputText.trim() || isThinking || !canChat}
              title="Send message"
              aria-label="Send"
            >
              <IcSend />
            </button>
          </div>
        </div>

      </div>
    </>
  );
};

export default GlobalAIAssistant;
